import { FGListen, FGMaskInput, FGSelectInput, showError, showSuccess } from "nsitools-react";
import * as React from "react";
import styled from "styled-components";
import * as Yup from "yup";

import { DogTitlesApi, EPlatform, ErrorDetails, SaveNewDogTitleDto } from "../../../../../../api";
import { FGCountrySelect, SmallFormGenerator } from "../../../../../../components";
import { useGlobalData } from "../../../../../../contexts";
import { useApiService, useTl } from "../../../../../../hooks";
import { useReferential } from "../../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../../locales";

export interface IDogTitlesPanelProps {
  onTitleSelected: (title: SaveNewDogTitleDto) => void;
  dogId: number;
  onSaved: () => void;
}

const StyledContainer = styled.div`
  margin-top: 1rem;
`;

export const DogTitlesPanel: React.FunctionComponent<IDogTitlesPanelProps> = ({ dogId, onTitleSelected, onSaved }) => {
  const { t, tUnsafe } = useTl();
  const { currentLanguage } = useGlobalData();
  const dogTitleApi = useApiService(DogTitlesApi);

  const [year, setYear] = React.useState(null);

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      titleId: Yup.number()
        .nullable()
        .required(t(ETLCodes.Required))
        .test("title-available", t(ETLCodes.DogAlreadyHasTitle), async function (value) {
          const res = await dogTitleApi.isTitleAvailable(dogId, value, year ?? 0);
          return res.value;
        }),
      countryId: Yup.number().nullable(),
    });
  }, [dogId, dogTitleApi, t, year]);

  const titleApi = useApiService(DogTitlesApi);
  const [countryId, setCountryId] = React.useState<number>();

  const [titles, tloading] = useReferential(
    (a) => a.getTitles(currentLanguage.languageId, EPlatform.CARE4DOGS, countryId, null),
    true,
    [currentLanguage.languageId, countryId]
  );

  const save = React.useCallback(
    async (data: any) => {
      try {
        const res = await titleApi.saveNewDogTitle(data).then(() => onSaved());
        showSuccess(t(ETLCodes.SaveSuccess));
        return res;
      } catch (error) {
        const details: ErrorDetails = JSON.parse(error.response);
        if (details?.errorCode) {
          showError(tUnsafe(`BackendError_${details.errorCode}`));
        } else {
          showError(t(ETLCodes.Error));
        }
      }
    },
    [onSaved, t, tUnsafe, titleApi]
  );

  const initialData = React.useMemo(() => new SaveNewDogTitleDto({ dogId: dogId, resultIds: [] }).toJSON(), [dogId]);

  return (
    <StyledContainer>
      <SmallFormGenerator
        onSubmit={save}
        initialValues={initialData}
        editMode={true}
        validationSchema={FormSchema}
        showDeleteButton={false}
        hideButtons={false}
        minLabelWidth={110}
      >
        <FGListen field="countryId" onChanged={(value) => setCountryId(value)} />
        <FGListen field="year" onChanged={(value) => setYear(value)} />
        <FGCountrySelect name="countryId" label={t(ETLCodes.Country)} />
        <FGSelectInput name="titleId" items={titles} loading={tloading} label={t(ETLCodes.Title)} />
        <FGMaskInput label={t(ETLCodes.Year)} name="year" cleaveOptions={{ numericOnly: true }} />
      </SmallFormGenerator>
    </StyledContainer>
  );
};
